import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import { api } from './api';

/**
 * Loads `path` and keeps it fresh: every `pollMs` while the app is in front, and again
 * whenever the app comes back to the front. `refresh()` is for pull-to-refresh.
 */
export function useData<T>(path: string | null, pollMs = 0) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!path);
  const [refreshing, setRefreshing] = useState(false);
  const pathRef = useRef(path);
  pathRef.current = path;

  const load = useCallback(async (manual = false) => {
    const p = pathRef.current;
    if (!p) return;
    if (manual) setRefreshing(true);
    try {
      const d = await api<T>(p);
      if (pathRef.current === p) {
        setData(d);
        setError(null);
      }
    } catch (e: any) {
      if (pathRef.current === p) setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
  }, [path, load]);

  useEffect(() => {
    if (!pollMs) return;
    const timer = setInterval(() => {
      if (AppState.currentState === 'active') load();
    }, pollMs);
    const sub = AppState.addEventListener('change', (s) => s === 'active' && load());
    return () => {
      clearInterval(timer);
      sub.remove();
    };
  }, [pollMs, load]);

  return { data, error, loading, refreshing, refresh: () => load(true), reload: () => load() };
}
